import "./din276Tree.css";
import { type ReactNode } from "react";
import { ChevronRight, ChevronDown, Boxes, MapPin, Scale, RefreshCw, Link2, SlidersHorizontal } from "lucide-react";

import MaterialSymbol from "../../components/MaterialSymbol";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import Datasheet from "./Din276Datasheet";
import { matchNorm, isHarmonized, type DinAttr, type DinNode, type DinTree } from "./din276Tree";

/**
 * One row of the DIN 276 tree — class or part, with its norm chip, where-used count and the
 * collapsible Attribute / Teile / Verweise sections.
 *
 * Shared by the DIN 276 tree of the administration and the data model of the operator obligations,
 * so both show a class the same way. The data model passes `ownAttrs` in the context: the
 * attributes the customer added, shown in the datasheet with a chip of their own.
 */

export const classKey = (code: string) => `c:${code}`;
export const partKey = (parent: string, slot: string, target: string) => `${parent}>${slot}#${target}`;
export const kindClass = (kind: string) =>
  kind === "component" ? "component" : kind === "locus" ? "locus" : kind === "funktion" ? "funktion" : "element";


/** Norm chip colour bucket from the source string. */
export function normKindClass(source?: string): string {
  const s = (source || "").toUpperCase();
  if (/H?EPN|HEN|EN 14351|EN 16034|EN 1125|EN 179/.test(s)) return "hen";
  if (s.startsWith("EN ")) return "en";
  if (s.startsWith("DIN ")) return "din";
  if (s.startsWith("ASR")) return "asr";
  if (s.includes("DGUV")) return "dguv";
  if (s.includes("GEFMA")) return "gefma";
  if (s.startsWith("TRI")) return "tri";
  return "other";
}

/** Collapsible sub-section header (Attribute / Teile / Verweise). */
export function SectionHead(props: { label: ReactNode; open: boolean; indent: number; onToggle: () => void; tone?: string }) {
  return (
    <button className={`din276tree-section tone-${props.tone || "attr"}`} style={{ paddingLeft: props.indent }} onClick={props.onToggle}>
      {props.open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
      <span className="din276tree-section-label">{props.label}</span>
    </button>
  );
}

export interface RowCtx {
  tree: DinTree;
  expanded: Set<string>;
  toggle: (k: string) => void;
  whereOpen: string | null;
  setWhereOpen: (k: string | null) => void;
  revealParent: (owner: string, slot: string, target: string) => void;
  flash: string | null;
  matchSet: Set<string>;
  isHidden: (code: string) => boolean;
  /**
   * Attributes a customer added to a class — shown in its datasheet after the ones the norm gives,
   * each with a chip that says where it comes from. Absent in the administration.
   */
  ownAttrs?: (code: string) => DinAttr[];
  /** The text on that chip, e.g. the customer's name. */
  ownLabel?: string;
}

export function Row(props: {
  ctx: RowCtx;
  code: string;
  depth: number;
  nodeKey: string;
  isPart: boolean;
  slotName?: string;
  cardinality?: string;
  seen: Set<string>;
}) {
  const { ctx, code, depth, nodeKey, isPart, slotName, cardinality, seen } = props;
  const node: DinNode | undefined = ctx.tree.byCode[code];
  if (!node) {
    return (
      <div className="din276tree-row" style={{ paddingLeft: 8 + depth * 18 }}>
        <span className="din276tree-code">{code}</span>
        <span className="din276tree-mute text-xs"> — unbekanntes Ziel</span>
      </div>
    );
  }
  const cyclic = seen.has(code);
  const classChildren = isPart ? [] : node.children;
  const hasParts = node.slots.length > 0 && !cyclic;
  const ownAttrs = ctx.ownAttrs?.(code) ?? [];
  const attrCount = node.attrs.length + ownAttrs.length;
  const hasDetail = attrCount > 0 || node.refs.length > 0;
  const expandable = classChildren.length > 0 || hasParts || hasDetail;
  const open = ctx.expanded.has(nodeKey);
  const isMatch = ctx.matchSet.has(code);
  const norm = matchNorm(node.source);
  const nextSeen = new Set(seen).add(code);
  // Sub-sections: Attribute & Teile open by default, Verweise closed.
  const attrOpen = !ctx.expanded.has(`${nodeKey}#attr`);
  const partsOpen = !ctx.expanded.has(`${nodeKey}#parts`);
  const refsOpen = ctx.expanded.has(`${nodeKey}#refs`);
  const secIndent = 8 + (depth + 1) * 18;

  return (
    <>
      <div
        className={`din276tree-row${ctx.flash === nodeKey ? " flash" : ""}${isMatch ? " match" : ""}`}
        style={{ paddingLeft: 8 + depth * 18 }}
        data-key={nodeKey}
      >
        <button
          className="din276tree-twisty"
          aria-label={open ? "collapse" : "expand"}
          onClick={() => expandable && ctx.toggle(nodeKey)}
          disabled={!expandable}
          style={{ visibility: expandable ? "visible" : "hidden" }}
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>

        {node.icon ? (
          <MaterialSymbol
            name={node.icon}
            color={node.color}
            size={16}
            title={node.label}
          />
        ) : (
          <span className="din276tree-icn-spacer" />
        )}

        {isPart && slotName && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="din276tree-slotname">{slotName} →</span>
            </TooltipTrigger>
            <TooltipContent>
              <div><b>{slotName}</b> → {node.label}</div>
              <div>Kardinalität {cardinality || "—"} — strukturell; <i>min</i> kommt aus der Pflicht, nicht aus der Struktur.</div>
            </TooltipContent>
          </Tooltip>
        )}

        <span className={`din276tree-code kind-text-${kindClass(node.kind)}`}>{code}</span>
        <span className="din276tree-label">{node.label}</span>

        {cardinality && isPart && <span className="din276tree-card">[{cardinality}]</span>}

        {node.source && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={`din276tree-norm norm-${normKindClass(node.source)}`}>
                <Scale size={11} />
                {node.source}
                {isHarmonized(node.source) && <span className="din276tree-hen">hEN</span>}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <div><b>{node.source}</b>{norm ? ` — ${norm.title}` : ""}</div>
              {norm && <div>regelt: {norm.governs}</div>}
              {isHarmonized(node.source) && <div>harmonisiert (hEN/hEPN) — Traceability-relevant</div>}
            </TooltipContent>
          </Tooltip>
        )}

        {node.reuse > 1 && (
          <button
            className="din276tree-reuse"
            onClick={() => ctx.setWhereOpen(ctx.whereOpen === nodeKey ? null : nodeKey)}
            title="Wo verwendet"
          >
            ×{node.reuse}
          </button>
        )}

        {cyclic && (
          <span className="din276tree-cyc text-xs">
            <RefreshCw size={11} /> zyklisch
          </span>
        )}
      </div>

      {/* Where-used list */}
      {ctx.whereOpen === nodeKey && node.reuse > 1 && (
        <div className="din276tree-where" style={{ paddingLeft: 8 + (depth + 1) * 18 }}>
          <div className="din276tree-where-head text-xs din276tree-mute">
            {node.reuse}× verwendet · ein Typ, viele Verwendungen
          </div>
          {(ctx.tree.usedBy[code] || []).map((u, i) => (
            <button key={i} className="din276tree-where-item text-xs" onClick={() => ctx.revealParent(u.owner, u.slot, code)}>
              <span className="din276tree-code">{u.owner}</span>{" "}
              {ctx.tree.byCode[u.owner]?.label || ""}{" "}
              <span className="din276tree-mute">· {u.slot} [{u.cardinality || "—"}]</span>
            </button>
          ))}
        </div>
      )}

      {open && (
        <>
          {/* ── Attribute (datasheet) ── */}
          {attrCount > 0 && (
            <>
              <SectionHead
                label={<><SlidersHorizontal size={12} /> Attribute ({attrCount})</>}
                open={attrOpen}
                indent={secIndent}
                onToggle={() => ctx.toggle(`${nodeKey}#attr`)}
                tone="attr"
              />
              {attrOpen && (
                <Datasheet attrs={node.attrs} ownAttrs={ownAttrs} ownLabel={ctx.ownLabel} indent={secIndent + 18} />
              )}
            </>
          )}

          {/* ── Teile (composition — the part drilldown) ── */}
          {hasParts && (
            <>
              <SectionHead
                label={<><Boxes size={12} /> Teile ({node.slots.length})</>}
                open={partsOpen}
                indent={secIndent}
                onToggle={() => ctx.toggle(`${nodeKey}#parts`)}
                tone="parts"
              />
              {partsOpen &&
                node.slots.map((s, i) => (
                  <Row
                    key={`p-${i}-${s.target}`}
                    ctx={ctx}
                    code={s.target}
                    depth={depth + 2}
                    nodeKey={partKey(nodeKey, s.name, s.target)}
                    isPart
                    slotName={s.name}
                    cardinality={s.cardinality}
                    seen={nextSeen}
                  />
                ))}
            </>
          )}

          {/* ── Verweise ── */}
          {node.refs.length > 0 && (
            <>
              <SectionHead
                label={<><Link2 size={12} /> Verweise ({node.refs.length})</>}
                open={refsOpen}
                indent={secIndent}
                onToggle={() => ctx.toggle(`${nodeKey}#refs`)}
                tone="refs"
              />
              {refsOpen &&
                node.refs.map((r, i) => (
                  <div key={`ref-${i}`} className="din276tree-row din276tree-verweis" style={{ paddingLeft: secIndent + 18 }}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="din276tree-mute text-xs"><MapPin size={11} /> {r.name} {r.target}</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Liegt am Gebäude/Standort — hier nur Verweis. Die Pflicht liest dies, das Bauteil dupliziert es nicht.
                      </TooltipContent>
                    </Tooltip>
                  </div>
                ))}
            </>
          )}

          {/* ── Classification children (DIN tree, not for parts) ── */}
          {classChildren
            .filter((child) => !ctx.isHidden(child))
            .map((child) => (
              <Row key={`c-${child}`} ctx={ctx} code={child} depth={depth + 1} nodeKey={classKey(child)} isPart={false} seen={nextSeen} />
            ))}
        </>
      )}
    </>
  );
}
